import { QueryClient } from "@tanstack/react-query";

import { DocumentQuery, DocumentsQuery } from "../../../generated/app-service-gql/graphql";
import { DocumentDetails } from "./useDocument";
import { DocumentsDto } from "./useDocuments";

/**
 * Sets hasBuyerLeads = true for all cached documents whose buyers include any of the given
 * organisation IDs. Safe to pass both buyer and supplier IDs — only documents where the
 * ID appears in the buyers array are updated.
 */
export function updateCachedDocumentsHasBuyerLeads(
  queryClient: QueryClient,
  organisationIds: string[],
) {
  if (organisationIds.length === 0) return;
  const buyerIdSet = new Set(organisationIds);
  const documentHasMatchingBuyer = (buyers: { id: string }[]) =>
    buyers.some((b) => buyerIdSet.has(b.id));

  queryClient.setQueriesData<DocumentsQuery>({ queryKey: ["documents"], exact: false }, (data) => {
    const results = data?.documents?.results;
    if (!results || results.length === 0) return data;

    let anyUpdated = false;
    const updatedResults = results.map((r) => {
      if (r.hasBuyerLeads) return r;
      if (!documentHasMatchingBuyer(r.buyers ?? [])) return r;
      anyUpdated = true;
      return { ...r, hasBuyerLeads: true };
    });

    if (!anyUpdated) return data;

    return {
      ...data,
      documents: {
        ...data.documents,
        results: updatedResults,
      },
    };
  });

  for (const query of queryClient
    .getQueryCache()
    .findAll({ queryKey: ["document"], exact: false })) {
    queryClient.setQueryData<DocumentQuery>(query.queryKey, (data) => {
      if (!data?.document || data.document.hasBuyerLeads) return data;
      if (!documentHasMatchingBuyer(data.document.buyers ?? [])) return data;
      return { ...data, document: { ...data.document, hasBuyerLeads: true } };
    });
  }
}

export function updateCachedDocument({
  queryClient,
  documentId,
  updateDocument,
}: {
  queryClient: QueryClient;
  documentId: string;
  updateDocument: (r: DocumentDetails) => DocumentDetails;
}) {
  queryClient.setQueryData<DocumentQuery>(["document", documentId], (resp) => {
    if (!resp?.document) {
      return resp;
    }

    return { ...resp, document: updateDocument(resp.document) };
  });
}

/**
 * Updates the cached documents data for a specific document using the provided updater function.
 * @param param0
 */
export function updateCachedDocuments({
  queryClient,
  documentId,
  updateDocumentsRow,
  updateDocumentQuery,
}: {
  queryClient: QueryClient;
  documentId: string;
  updateDocumentsRow: (r: DocumentsDto) => DocumentsDto;
  updateDocumentQuery?: (r: DocumentDetails) => DocumentDetails;
}) {
  queryClient.setQueriesData<DocumentsQuery>({ queryKey: ["documents"], exact: false }, (data) => {
    const results = data?.documents?.results;
    if (!results || results.length === 0) return data;

    const index = results.findIndex((c) => c.id === documentId);

    if (index === -1) return data;

    const nextRow = updateDocumentsRow(results[index]);

    // updater must return the same reference object to signal "no change" to the cache
    if (nextRow === results[index]) return data;

    const nextResults = results.slice();
    nextResults[index] = nextRow;

    return {
      ...data,
      documents: {
        ...data.documents,
        results: nextResults,
      },
    };
  });

  if (updateDocumentQuery && documentId) {
    updateCachedDocument({
      queryClient,
      documentId,
      updateDocument: updateDocumentQuery,
    });
  }
}
